import { FacebookLoginProvider, SocialAuthService } from '@abacritt/angularx-social-login';
import { Clipboard } from '@angular/cdk/clipboard';
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import * as Color from 'color';
import * as moment from 'moment';
import { FileSaverService } from 'ngx-filesaver';
import { NgxSpinnerService } from 'ngx-spinner';
import { DialogInfoComponent } from 'src/app/shared/components/dialog-info/dialog-info.component';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { environment } from 'src/environments/environment';

import { Lingua } from '../models/bo.model';
import { DialogOptions, Errore, Filter, ScriptParameter } from '../models/dto';
import { FilterValueType, TipoModalitaPagamento } from '../models/enum';

@Injectable({
  providedIn: 'root'
})
export class UtilityService {

  public today: moment.Moment;
  public tomorrow: moment.Moment;
  public startOfMonth: moment.Moment;
  public endOfMonth: moment.Moment;
  public oneMonthFromToday: moment.Moment;
  public oneYearFromToday: moment.Moment;
  public oneMonthBeforeToday: moment.Moment;
  public startOfYear: moment.Moment;

  public loadedScripts: string[] = [];
  public svgCache: Map<string, string> = new Map();

  public modalitaPagamentoPost: TipoModalitaPagamento[] = [TipoModalitaPagamento.XPay, TipoModalitaPagamento.PayWay, TipoModalitaPagamento.Unicredit, TipoModalitaPagamento.TPay, TipoModalitaPagamento.Stripe, TipoModalitaPagamento.BancaSella, TipoModalitaPagamento.Holipay, TipoModalitaPagamento.Siapay, TipoModalitaPagamento.ScalaPay, TipoModalitaPagamento.VirtualPay, TipoModalitaPagamento.MyPOS, TipoModalitaPagamento.VivaWallet];

  public get lingue() {
    return this.localizationService.languages;
  }

  constructor(
    private http: HttpClient,
    private matDialog: MatDialog,
    private localizationService: LocalizationService,
    private fileSaverService: FileSaverService,
    private ngxSpinnerService: NgxSpinnerService,
    private clipboard: Clipboard,
    private snackBarService: MatSnackBar,
    private authService: SocialAuthService,
    private router: Router
  ) {
    this.today = moment();
    this.tomorrow = moment().add(1, 'day');
    this.oneMonthBeforeToday = moment().subtract(1, 'month');
    this.oneMonthFromToday = moment().add(1, 'month');
    this.startOfMonth = moment().set('date', 1);
    this.endOfMonth = moment().set('date', moment().daysInMonth());
    this.startOfYear = moment().set('date', 1).set('month', 0);
    this.oneYearFromToday = moment().add(1, 'year');
  }

  getFilters(filters: Filter[]): Filter[] {
    if (filters != null) {
      filters = filters.filter(x => {
        let isInvertedFilter = x.invertedOperator >= 0;

        if (isInvertedFilter) {
          return x.active || x.inverted;
        } else {
          return (x.value != null && x.value.toString() != '') || x.active;
        }
      });

      filters.filter(x => x.valueType == FilterValueType.Date).forEach(x => x.value = this.getDate(x.value));
    }

    return filters;
  }

  capitalize(value: string): string {
    return value[0].toUpperCase() + value.substring(1);
  }

  uncapitalize(value: string): string {
    return value[0].toLowerCase() + value.substring(1);
  }

  capitalizeField(value: string): string {
    let ret = '';
    let fieldSplit = value.split('.');

    for (let field of fieldSplit) {
      ret += this.capitalize(field) + '.';
    }

    return ret.slice(0, -1);
  }

  uncapitalizeField(value: string): string {
    let ret = '';
    let fieldSplit = value.split('.');

    for (let field of fieldSplit) {
      ret += this.uncapitalize(field) + '.';
    }

    return ret.slice(0, -1);
  }

  getEnumKeyTranslation(en: object, value: any, enumName?: string): string {
    let keys = Object.keys(en).filter(x => en[x] == value);

    if (enumName != null) {
      let key = keys.length > 0 ? keys[0] : null;

      if (key != null) {
        let uncapitalizedStringValue = '';

        if (key.length > 1) {
          uncapitalizedStringValue = (key[0] == key[0].toUpperCase() && key[1] == key[1].toUpperCase()) ? key : key.charAt(0).toLowerCase() + key.slice(1);
        } else if (key.length == 1) {
          uncapitalizedStringValue = key.toLowerCase();
        }

        return 'enumerations.' + enumName + '.' + uncapitalizedStringValue;
      } else {
        return null;
      }
    }

    return keys.length > 0 ? keys[0] : null;
  }

  getEnumKey(en: object, value: any): string {
    let keys = Object.keys(en).filter(x => en[x] == value);
    return keys.length > 0 ? this.uncapitalize(keys[0]) : null;
  }

  getEnumKeyOriginal(en: object, value: any): string {
    let keys = Object.keys(en).filter(x => en[x] == value);
    return keys.length > 0 ? keys[0] : null;
  }

  randomIntFromInterval(min: number, max: number) {
    return Math.floor(Math.random() * (max - min + 1) + min);
  }

  getRandomImage(): string {
    return "url('https://assets.passepartout.cloud/sfondi/sfondo" + this.randomIntFromInterval(1, 26) + ".jpg')"
  }

  findIndexByOid(array: any[], oid: number): number {
    if (array != null && array.length > 0 && array[0].oid != null && oid != null && oid > 0) {
      return array.findIndex(x => x.oid == oid);
    }

    return -1;
  }

  findItemByOid(array: any[], oid: number): any {
    if (array != null && array.length > 0 && array[0].oid != null && oid != null && oid > 0) {
      return array.find(x => x.oid == oid);
    }

    return null;
  }

  findItemsByOid(array: any[], oids: number[]): any[] {
    if (array != null && array.length > 0 && array[0].oid != null && oids != null && oids.length > 0) {
      let ret: any[] = [];

      for (let oid of oids) {
        ret.push(this.findItemByOid(array, oid));
      }

      return ret;
    }

    return null;
  }

  validateEmail(email: string): boolean {
    return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(email);
  }

  validatePassword(password: string): boolean {
    return /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[a-zA-Z])(?=.*[!"#$%&'()*+,-.\/:;<=>?@[\\\]^_`{|}~]).{8,}$/.test(password);
  }

  counter(count: number, from: number = 0, increment: number = 1): number[] {
    let rs: number[] = [];

    for (let x = from, y = 0; y < count; x += increment, y++) {
      rs.push(x);
    }

    return rs;
  }

  counterFromTo(from: number, to: number): number[] {
    let rs: number[] = [];

    for (let x = from; x <= to; x++) {
      rs.push(x);
    }

    return rs;
  }

  loadExternalScript(scriptUrl: string, parameters?: ScriptParameter[], id?: string): Promise<void> {
    return new Promise<void>((resolve) => {
      if (this.loadedScripts.includes(scriptUrl)) {
        resolve();
      } else {
        this.createScriptElement(scriptUrl, parameters, null, id).then(() => {
          this.loadedScripts.push(scriptUrl);
          resolve();
        }, () => resolve());
      }
    });
  }

  loadScriptCode(scriptCode: string, scriptPath?: string, id?: string): Promise<void> {
    return new Promise<void>((resolve) => {
      if (!!scriptCode) {
        this.createScriptElement(null, null, scriptCode).then(() => resolve(), () => resolve());
      } else {
        this.http.get(scriptPath, { responseType: 'text' }).subscribe({
          next: (data: any) => {
            if (!!data) {
              this.createScriptElement(null, null, data, id).then(() => resolve(), () => resolve());
            } else {
              resolve();
            }
          },
          error: () => resolve()
        });
      }
    });
  }

  private createScriptElement(scriptUrl: string, parameters?: ScriptParameter[], scriptCode?: string, id?: string): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      if (!!scriptUrl || !!scriptCode) {
        if (!!id) {
          let element = document.getElementById(id);

          if (element != null && element.tagName == 'script') {
            element.remove();

            let index = this.loadedScripts.findIndex(x => x == (element as HTMLScriptElement).src);

            if (index >= 0) {
              this.loadedScripts.splice(index, 1);
            }
          }
        }

        let scriptElement = document.createElement('script');

        if (!!id) {
          scriptElement.id = id;
        }

        if (!!scriptUrl) {
          scriptElement.src = scriptUrl;
        } else {
          scriptElement.innerText = scriptCode;
        }

        if (parameters != null && parameters.length > 0) {
          for (let parameter of parameters) {
            if (!!parameter.name) {
              scriptElement.setAttribute(parameter.name, parameter.value || '');
            }
          }
        }

        scriptElement.onload = () => resolve();
        document.getElementsByTagName('head')[0].appendChild(scriptElement);
      } else {
        reject();
      }
    });
  }

  invertColor(colorHex: string, bw: boolean = true): string {
    if (colorHex != null) {
      let color = Color('#FFFFFF');

      try {
        color = Color(colorHex);
      } catch (e) { }

      let r = color.red();
      let g = color.green();
      let b = color.blue();

      if (bw) {
        if (color.luminosity() <= 0.5) {
          return '#FFFFFF';
        } else {
          return '#000000';
        }
      }

      let ret = Color({ r: 255 - r, g: 255 - g, b: 255 - b });

      return ret.hex();
    }

    return null;
  }

  getValuta(valuta: string, space: boolean = true): string {
    let ret = '€';

    if (!!valuta) {
      ret = valuta;
    }

    if (space) {
      ret += ' ';
    }

    return ret;
  }

  getPriceIntegerPart(price: number, abs: boolean = false): string {
    if (abs) {
      price = Math.abs(price);
    }

    let integerPart = Math.floor(price);
    let decimalPart = Math.round((price - integerPart) * 100);

    if (decimalPart >= 100) {
      integerPart += 1;
    }

    return integerPart.toString();
  }

  getPriceDecimalPart(price: number, abs: boolean = false): string {
    if (abs) {
      price = Math.abs(price);
    }

    let decimals = Math.round(((price - Math.floor(price)) * 100)).toString();

    if (decimals.length == 1) {
      return '0' + decimals;
    } else if (decimals.length >= 3) {
      return '00';
    } else {
      return decimals;
    }
  }

  openUrlInNewTab(url: string) {
    if (!!url) {
      window.open(url);
    }
  }

  isNullDate(date: string): boolean {
    if (!!date) {
      let d: moment.Moment;

      try {
        d = moment(date);
      } catch (e) {
        return true;
      }

      if (d != null && d.isValid()) {
        return d.date() == 1 && d.month() == 0 && d.year() == 1;
      }
    }

    return true;
  }

  isValidDate(date: string): boolean {
    if (!!date) {
      let d: moment.Moment;

      try {
        d = moment(date);
      } catch (e) {
        return false;
      }

      if (d == null || !d.isValid()) {
        return false;
      }
    }

    return true;
  }

  tableToCSV(rows: any[], columns?: string[], separator: string = ','): string {
    if (rows != null && rows.length > 0 && (columns == null || columns.length > 0) && !!separator) {
      let csv: string = 'sep=' + separator + '\n';

      if (columns == null) {
        for (let property in rows[0]) {
          csv += property + separator;
        }
      } else {
        for (let property of columns) {
          csv += property + separator;
        }
      }

      csv = csv.slice(0, -1) + '\n';

      for (let row of rows) {
        if (columns == null) {
          for (let property in row) {
            let value = this.getPropertyValue(row, property);
            csv += (value != null ? value : '') + separator;
          }
        } else {
          for (let property of columns) {
            let value = this.getPropertyValue(row, property);
            csv += (value != null ? value : '') + separator;
          }
        }

        csv = csv.slice(0, -1) + '\n';
      }

      csv = csv.slice(0, -1);

      return csv;
    }

    return null;
  }

  getPropertyValue(obj: any, name: string): string {
    let propertySplit = name.split('.');

    let value: any = obj;

    for (let property of propertySplit) {
      if (value != null) {
        value = value[property];
      } else {
        return '';
      }
    }

    return value;
  }

  exportToCsv(rows: any[], fileName: string, columns?: string[]) {
    let csv = this.tableToCSV(rows, columns);

    if (!!csv) {
      this.fileSaverService.save(new Blob([csv], { type: 'text/csv' }), fileName + '.csv');
    }
  }

  extractTagsFromHtml(html: string, tags: string[]): string[] {
    let ret: string[] = [];
    let parser = new DOMParser();
    let parsedHtml = parser.parseFromString(html, "text/html");

    for (let tag of tags) {
      let elements = Array.from(parsedHtml.getElementsByTagName(tag));
      elements.forEach(x => ret.push(x.outerHTML));
    }

    return ret;
  }

  removeTagFromHtml(html: string, tag: string): string {
    let parser = new DOMParser();
    let parsedHtml = parser.parseFromString(html, "text/html");

    let elements = Array.from(parsedHtml.getElementsByTagName(tag));

    elements.forEach(x => x.remove());

    return parsedHtml.documentElement.innerHTML;
  }

  htmlContainsTag(html: string, tag: string): boolean {
    let parser = new DOMParser();
    let parsedHtml = parser.parseFromString(html, "text/html");

    let elements = Array.from(parsedHtml.getElementsByTagName(tag));

    return elements.length > 0;
  }

  removeTagsFromHtml(html: string, tags: string[]): string {
    let parser = new DOMParser();
    let parsedHtml = parser.parseFromString(html, "text/html");

    for (let tag of tags) {
      let elements = Array.from(parsedHtml.getElementsByTagName(tag));
      elements.forEach(x => x.remove());
    }

    return parsedHtml.documentElement.innerHTML;
  }

  htmlContainsTags(html: string, tags: string[]): boolean {
    let parser = new DOMParser();
    let parsedHtml = parser.parseFromString(html, "text/html");

    for (let tag of tags) {
      let elements = Array.from(parsedHtml.getElementsByTagName(tag));

      if (elements.length > 0) {
        return true;
      }
    }

    return false;
  }

  parametersStringToJson(parametersString: string): any {
    if (!!parametersString) {
      let rs: any = {};

      parametersString = parametersString.replace('?', '');

      let parameters = parametersString.split('&');

      for (let parameter of parameters) {
        let parameterSplit = parameter.split('=');

        if (parameterSplit.length == 2) {
          rs[parameterSplit[0]] = parameterSplit[1];
        }
      }

      return rs;
    }

    return null;
  }

  parametersJsonToString(parameters: any): string {
    if (parameters != null) {
      let rs: string = '?';

      for (let parameter in parameters) {
        rs += parameter + '=' + parameters[parameter] + '&';
      }

      if (rs.length > 0) {
        rs = rs.substring(0, rs.length - 1);
      }

      return rs;
    }

    return null;
  }

  generateRandomString(length: number = 8) {
    let res: string = '';
    let alphabet: string = 'abcdefghijklmnopqrstuvwxyz0123456789';

    for (let i = 0; i < length; i++) {
      res += alphabet.charAt(Math.floor(Math.random() * alphabet.length));
    }

    return res;
  }

  roundTo(value: number, decimals: number): number {
    let factor: number = Math.pow(10, decimals);
    return Math.round(value * factor) / factor;
  }

  clamp(value: number, min?: number, max?: number): number {
    let ret = value;

    if (value != null) {
      if (min != null) {
        ret = Math.max(ret, min);
      }

      if (max != null) {
        ret = Math.min(ret, max);
      }
    }

    return ret;
  }

  checkCellulare(cellulare: string): string {
    if (!!cellulare) {
      cellulare = cellulare.replace(/\s/g, '');

      if (cellulare.startsWith('+')) {
        cellulare = cellulare.substring(1);
      } else if (cellulare.length <= 10 || !cellulare.startsWith('39')) {
        cellulare = '39' + cellulare;
      }
    }

    return cellulare;
  }

  checkOrario(orario: string, orarioMinimo: string, orarioMassimo?: string): string {
    if (!!orario) {
      let ora = moment(orario, 'hh:mm');

      if (!!orarioMinimo) {
        if (ora.isBefore(moment(orarioMinimo, 'hh:mm'))) {
          orario = orarioMinimo;
        }
      }

      if (!!orarioMassimo) {
        if (ora.isAfter(moment(orarioMassimo, 'hh:mm'))) {
          orario = orarioMassimo;
        }
      }
    }

    return orario;
  }

  toDate(date: moment.Moment): Date {
    if (date != null) {
      return new Date(date.year(), date.month(), date.date());
    }

    return null;
  }

  getSelection(): string {
    if (window.getSelection) {
      return window.getSelection().toString();
    }

    return '';
  }

  clearSelection() {
    if (window.getSelection) {
      if (window.getSelection().empty) {
        window.getSelection().empty();
      } else if (window.getSelection().removeAllRanges) {
        window.getSelection().removeAllRanges();
      }
    }
  }

  checkClick(event: any): boolean {
    let isButton = !!event.target.classList && event.target.classList.contains('mat-mdc-focus-indicator');

    if (isButton) {
      this.clearSelection();
      return true;
    } else {
      return this.getSelection().length == 0;
    }
  }

  callNumber(cellulare: string) {
    if (!!cellulare) {
      window.location.href = 'tel:' + cellulare;
    }
  }

  sendEmail(email: string) {
    if (!!email) {
      window.location.href = 'mailto:' + email;
    }
  }

  showModalDialog(componentRef: any, options: DialogOptions): MatDialogRef<{}, any> {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.closeOnNavigation = true;
    dialogConfig.disableClose = false;
    dialogConfig.data = options;
    dialogConfig.autoFocus = true;
    dialogConfig.maxWidth = '100vw';
    dialogConfig.maxHeight = '90vh';

    if (options.viewContainerRef != null) {
      dialogConfig.viewContainerRef = options.viewContainerRef;
    }

    if (options.disableClose != null) {
      dialogConfig.disableClose = options.disableClose;
    }

    if (options.hasBackdrop != null) {
      dialogConfig.hasBackdrop = options.hasBackdrop;
    }

    if (!!options.panelClass) {
      dialogConfig.panelClass = options.panelClass;
    }

    if (!!options.width) {
      dialogConfig.width = options.width;
    }

    if (!!options.minWidth) {
      dialogConfig.minWidth = options.minWidth;
    }

    if (!!options.minHeight) {
      dialogConfig.minHeight = options.minHeight;
    }

    if (!!options.maxHeight) {
      dialogConfig.maxHeight = options.maxHeight;
    }

    if (!!options.height) {
      dialogConfig.height = options.height;
    }

    if (options.dialogPosition != null) {
      dialogConfig.position = options.dialogPosition;
    }

    return this.matDialog.open(componentRef, dialogConfig);
  }

  setIsLoading(isLoading: boolean) {
    if (isLoading) {
      this.ngxSpinnerService.show();
    } else {
      this.ngxSpinnerService.hide();
    }
  }

  private showMessage(idMessage: string, isErrore: boolean, params?: any, title?: string): Promise<any> {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.closeOnNavigation = true;
    dialogConfig.disableClose = false;
    dialogConfig.minWidth = 250;

    let options = new DialogOptions(title != null ? title : '');
    options.message = idMessage;
    options.messageParams = params;
    options.isErrore = isErrore;
    options.isShowCancelButton = false;
    dialogConfig.data = options;

    let dialogRef = this.matDialog.open(DialogInfoComponent, dialogConfig);

    dialogRef.afterClosed().subscribe(() => {
      if (!dialogRef.componentInstance.isResolved) {
        dialogRef.componentInstance.resolve();
      }
    });

    return dialogRef.componentInstance.result;
  }

  showInfo(idMessage: string, params?: any, title?: string): Promise<any> {
    return this.showMessage(idMessage, false, params, title);
  }

  showError(idMessage: string, params?: any, title?: string): Promise<any> {
    return this.showMessage(idMessage, true, params, title);
  }

  showDialogConfirm(idMessage: string, params?: any): Promise<any> {
    let dialogConfig = new MatDialogConfig();
    dialogConfig.closeOnNavigation = true;
    dialogConfig.disableClose = true;
    dialogConfig.minWidth = 250;

    let options = new DialogOptions();
    options.message = idMessage;
    options.messageParams = params;
    options.isShowCancelButton = true;
    options.okButtonText = 'shared.dialog.yes';
    options.cancelButtonText = 'shared.dialog.no';
    dialogConfig.data = options;

    let dialogRef = this.matDialog.open(DialogInfoComponent, dialogConfig);

    return dialogRef.componentInstance.result;
  }

  private getUrlWithoutParameters(url: string): string {
    let index = url.indexOf('?');

    if (index >= 0) {
      url = url.substring(0, index);
    }

    return url;
  }

  openPageSbm(name: string, params: any = {}, replaceUrl: boolean = false): Promise<void> {
    let urlBeforeRedirect = this.getUrlWithoutParameters(window.location.href);

    return this.router.navigate(['sbm/home', { outlets: { homeOutlet: name } }], { queryParams: params ?? {}, replaceUrl: replaceUrl ?? false }).then(() => {
      if (this.getUrlWithoutParameters(window.location.href) == urlBeforeRedirect) {
        window.location.reload();
      }
    });
  }

  getUrlPageSbm(name: string, params: any = {}): string {
    return window.location.origin + this.router.createUrlTree(['sbm/home', { outlets: { homeOutlet: name } }], { queryParams: params ?? {} }).toString();
  }

  showSnackBar(message: string, duration: number = 2000) {
    this.snackBarService.open(this.localizationService.getTranslationSync(message), 'Ok', { duration: duration });
  }

  copyToClipboard(text: string) {
    if (!!text) {
      this.clipboard.copy(text);
    }
  }

  getDate(value: any, format: string = 'YYYY-MM-DD'): string {
    if (value) {
      return moment(value).format(format);
    }

    return null;
  }

  showErrors(errori: Errore[], title?: string): Promise<any> {
    return new Promise<any>(resolve => {
      if (errori != null && errori.length > 0) {
        this.showError(this.localizationService.getTranslationFromErrori(errori), null, title).then(x => resolve(x));
      } else {
        resolve(null);
      }
    });
  }

  showSequentialMessages(messages: string[], isErrore: boolean) {
    if (messages != null && messages.length > 0) {
      this.showSequentialMessage(messages, 0, isErrore)
    }
  }

  private showSequentialMessage(messages: string[], index: number, isErrore: boolean) {
    if (messages.length > index) {
      this.showMessage(messages[index], isErrore).then(() => {
        this.showSequentialMessage(messages, index + 1, isErrore);
      });
    }
  }

  getLocale(): string {
    return this.localizationService.getCurrentLocale();
  }

  setBackgroundImage(url: string): Promise<string> {
    let promise = new Promise<string>(resolve => {
      if (!!url) {
        //DA CONTROLLARE!!!
        /*let img = new Image();

        img.onload = () => {
          resolve('url(' + url + ')');
        };

        img.onerror = () => {
          resolve(null);
        };

        img.src = url;*/

        resolve('url(' + url + ')');
      } else {
        resolve(null);
      }
    });

    return promise;
  }

  setLingua(lingua: string): Promise<Lingua> {
    return new Promise<Lingua>(resolve => {
      let l: Lingua;

      if (!!lingua) {
        l = this.lingue.find(x => x.codice == lingua);
      }

      if (l == null) {
        l = this.lingue.find(x => x.codice == 'it');
      }

      this.localizationService.setDefaultLang(l.codice).then(() => {
        resolve(l);
      });
    });
  }

  updateLinguaUrl(lingua: string) {
    this.setIsLoading(true);

    setTimeout(() => {
      let url: string = window.location.href;

      let index = url.indexOf('lingua=');

      if (index >= 0) {
        let equalIndex = index + 'lingua='.length;
        let languageEndIndex = url.indexOf('&', equalIndex);

        if (languageEndIndex >= 0) {
          window.location.href = url.substring(0, equalIndex) + lingua + url.substring(languageEndIndex);
        } else {
          window.location.href = url.substring(0, equalIndex) + lingua;
        }
      } else {
        window.location.href = url + (window.location.href.includes('?') ? '&' : '?') + 'lingua=' + lingua;
      }
    }, 200);
  }

  signInWithFB() {
    this.authService.signOut(true).then(() => {
      this.authService.signIn(FacebookLoginProvider.PROVIDER_ID/*, { auth_type: 'reauthenticate' }*/).then(() => { }, () => { });
    }, () => {
      this.authService.signIn(FacebookLoginProvider.PROVIDER_ID/*, { auth_type: 'reauthenticate' }*/).then(() => { }, () => { });
    });
  }

  sendKeepAliveRequest(operationName: string, rq: any) {
    let requestUrl: string = environment.backendUrl + '/' + operationName;
    let body: string = JSON.stringify(rq);

    /*if (!navigator.sendBeacon(requestUrl, new Blob([body], { type: 'application/json' }))) {
      fetch(requestUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
    }*/

    fetch(requestUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body, keepalive: true });
  }

  exportHtml(nomeFile: string, html: string) {
    if (!!nomeFile && !!html) {
      let domParser = new DOMParser();
      let htmlDocument = domParser.parseFromString(html, 'text/html');

      if (htmlDocument != null) {
        let htmlString = '<!DOCTYPE html><html>';

        if (!!htmlDocument.head?.outerHTML) {
          htmlString += htmlDocument.head.outerHTML;
        }

        if (!!htmlDocument.body?.outerHTML) {
          htmlString += htmlDocument.body.outerHTML;
        }

        htmlString += '</html>';

        this.fileSaverService.save(new Blob([htmlString], { type: 'text/html' }), nomeFile + '.html');
      }
    }
  }

  loadFavicon(url: string) {
    let favicon = Array.from(document.getElementsByTagName('head')[0].getElementsByTagName('link')).find(x => x.rel == 'icon' && x.type == 'image/x-icon' && !!x.href && x.href.endsWith('/favicon.ico'));

    if (favicon != null) {
      favicon.href = url;
    }
  }
}